/**
 * Static seed data and reference definitions for all MaX Apps.
 * This file serves as:
 * 1. Fallback data for the Apps Hub if the API is unreachable or empty.
 * 2. Visual reference for category labels and colors.
 * 3. Source of truth for the 16 core modules in the MaX platform.
 */

export const MAX_APPS_SEED = [
  {
    code: "KAIZEN",
    name: "Kaizen",
    iconEmoji: "💡",
    colorHex: "#F59E0B",
    category: "Continuous Improvement",
    description: "Drive continuous improvement with structured Kaizen ideas, action tracking, and measurable results. Empower every team member to submit and implement improvements.",
    features: [
      "Submit Kaizen improvement ideas",
      "Multi-stage approval workflow",
      "Before/after photo documentation",
      "Impact & ROI tracking",
      "Team collaboration & reward system",
    ],
    version: "2.0.0",
    primaryPath: "/home/kaizen",
    navKeys: ["kaizen", "KAIZEN"],
  },
  {
    code: "OPL",
    name: "One Point Lesson",
    iconEmoji: "📖",
    colorHex: "#3B82F6",
    category: "Knowledge Management",
    description: "Create, manage, and share One-Point Lessons (OPLs) across your organization. Standardize knowledge transfer with visual documentation.",
    features: [
      "Visual SOP creation with rich content",
      "Categorize by type: Basic, Improvement, Problem",
      "Mobile-ready access for operators",
      "Version control and approvals",
      "Quiz and knowledge verification",
    ],
    version: "1.5.0",
    primaryPath: "/home/opl",
    navKeys: ["opl"],
  },
  {
    code: "RCA_5WHY",
    name: "5Why & Root Cause Analysis",
    iconEmoji: "🔍",
    colorHex: "#8B5CF6",
    category: "Continuous Improvement",
    description: "Systematically investigate problems using 5-Why Analysis and Fishbone Diagrams. Identify root causes and prevent recurrence.",
    features: [
      "Structured 5-Why analysis worksheets",
      "Interactive Fishbone (Ishikawa) diagrams",
      "Loss category linking for OEE",
      "Action tracking for corrective measures",
      "Historical RCA database and trends",
    ],
    version: "2.1.0",
    primaryPath: "/home/rca",
    navKeys: ["rca-records", "five-whys", "root-cause-categories", "failure-modes"],
  },
  {
    code: "CAPA",
    name: "CAPA Management",
    iconEmoji: "🛡️",
    colorHex: "var(--accent-primary)",
    category: "Continuous Improvement",
    description: "Manage Corrective and Preventive Actions (CAPA) with structured workflows, task management, and compliance tracking.",
    features: [
      "Centralized CAPA log and status tracking",
      "Risk-based priority assessment",
      "Automated due-date notifications",
      "Verification of effectiveness steps",
      "Audit-ready documentation and trails",
    ],
    version: "2.0.0",
    primaryPath: "/home/capa",
    navKeys: ["capa-records", "task-management"],
  },
  {
    code: "OEE_PRODUCTION",
    name: "Production & Downtime",
    iconEmoji: "📊",
    colorHex: "#EF4444",
    category: "Production",
    description: "Monitor Overall Equipment Effectiveness (OEE) in real-time. Track production runs, log breakdowns, and analyze efficiency losses.",
    features: [
      "Real-time OEE live dashboards",
      "Production entry for machine operators",
      "Downtime loss classification (6 Big Losses)",
      "Financial impact of secondary losses",
      "Drill-down reports: Trends, Summary, Loss",
    ],
    version: "3.0.0",
    primaryPath: "/home/oee",
    navKeys: ["oee-dashboard", "breakdown-log", "loss-reasons", "oee-settings"],
  },
  {
    code: "QUALITY_5S",
    name: "Quality / 5S Audit",
    iconEmoji: "✅",
    colorHex: "#06B6D4",
    category: "Quality",
    description: "Conduct digital quality checks and 5S workplace audits. Score areas, capture evidence, and track improvement actions.",
    features: [
      "Customizable 5S audit checklists",
      "Digital quality checksheets and logs",
      "Automatic scoring and benchmarking",
      "Photo evidence for non-conformities",
      "Action tracking and closure workflow",
    ],
    version: "1.0.0",
    primaryPath: "/home/fives",
    navKeys: ["fives", "QUALITY_5S"],
  },
  {
    code: "MACHINE_HEALTH",
    name: "Machine Health",
    iconEmoji: "🔧",
    colorHex: "#F97316",
    category: "Maintenance",
    description: "Comprehensive planned maintenance system — schedules, tasks, calendars, technician teams, and compliance tracking.",
    features: [
      "PM calendar with automated scheduling",
      "Operator daily and weekly PM tasks",
      "PM Standards & Master Data management",
      "Technician skills & team allocation",
      "MTBF/MTTR reliability analysis",
    ],
    version: "3.2.0",
    primaryPath: "/home/settings/pm-masters/pmStandards",
    navKeys: ["pm-hub", "MACHINE_HEALTH", "pm-operator-tasks", "pm-calendar", "pm-standards", "pm-masters"],
  },
  {
    code: "SAFETY_OBS",
    name: "Safety Observation",
    iconEmoji: "👁️",
    colorHex: "#14B8A6",
    category: "Safety",
    description: "Conduct behavioral safety observations, track safe/unsafe behaviors, and monitor safety analytics across the plant.",
    features: [
      "Digital safety observation rounds",
      "Peer-to-peer feedback and coaching",
      "Safety walk scheduling and campaigns",
      "Hazard heatmap and behavior trends",
      "Site-layout integration for locations",
    ],
    version: "2.5.0",
    primaryPath: "/home/safety-observation/schedule",
    navKeys: ["safety-observation-schedule", "safety-behavior-categories"],
  },
  {
    code: "HAZARD",
    name: "Hazard Reporting",
    iconEmoji: "⚠️",
    colorHex: "#F43F5E",
    category: "Safety",
    description: "Report workplace hazards instantly with severity assessment, photo evidence, and automated mitigation workflows.",
    features: [
      "Quick hazard report submission (mobile)",
      "Severity & likelihood risk assessment",
      "Automated alerts for high-risk hazards",
      "Mitigation tracking and verification",
      "Comprehensive safety analytics",
    ],
    version: "2.0.0",
    primaryPath: "/home/hazard-reports",
    navKeys: ["hazard-summary", "hazard-analytics", "hazard-categories"],
  },
  {
    code: "INCIDENT",
    name: "Incident Management",
    iconEmoji: "🚑",
    colorHex: "#DC2626",
    category: "Safety",
    description: "Complete incident lifecycle management — from initial reporting and investigation through root cause analysis and closure.",
    features: [
      "Step-by-step incident reporting wizard",
      "Investigation team & witness statements",
      "Integrated Root Cause Analysis (RCA)",
      "Formal recommendation and CAPA tracking",
      "Compliance and regulatory reporting",
    ],
    version: "2.0.0",
    primaryPath: "/home/incident/analytics",
    navKeys: ["incident-analytics", "incident-summary", "incident-new"],
  },
  {
    code: "WORK_ORDER",
    name: "Work Orders",
    iconEmoji: "🛠️",
    colorHex: "#6366F1",
    category: "Maintenance",
    description: "Manage breakdown and preventive work orders with a comprehensive wizard covering planning, safety, and execution.",
    features: [
      "9-step maintenance work order wizard",
      "Safety checklist & LOTO integration",
      "Resource and technician allocation",
      "Spare parts linkage and cost tracking",
      "Execution logs and history of repairs",
    ],
    version: "2.5.0",
    primaryPath: "/home/work-order",
    navKeys: ["work-order"],
  },
  {
    code: "SPARE_INVENTORY",
    name: "Spares & Inventory",
    iconEmoji: "📦",
    colorHex: "var(--accent-primary)",
    category: "Maintenance",
    description: "Track spare parts inventory, monitor stock levels, manage transactions, and automate re-order alerts.",
    features: [
      "Spare parts master data catalog",
      "Real-time stock level monitoring",
      "Transaction history (Inbound/Outbound)",
      "Minimum stock level alerts",
      "Inventory valuation and usage reports",
    ],
    version: "1.5.0",
    primaryPath: "/home/inventory/dashboard",
    navKeys: ["spare-parts", "inventory-dashboard", "inventory-history"],
  },
  {
    code: "FLOWCHART",
    name: "Smart Flowchart",
    iconEmoji: "🗺️",
    colorHex: "#D946EF",
    category: "Operations",
    description: "Build and visualize process flowcharts with a drag-and-drop builder. Map your production processes and OEE losses.",
    features: [
      "Drag-and-drop process layout builder",
      "Process, Decision, & IO node types",
      "Auto-layout with dagre engine",
      "High-resolution image export",
      "Linkage to physical plant processes",
    ],
    version: "1.0.0",
    primaryPath: "/home/flowchart",
    navKeys: ["flowchart"],
  },
  {
    code: "WORK_PERMIT",
    name: "Work Permits",
    iconEmoji: "🔒",
    colorHex: "#0EA5E9",
    category: "Safety",
    description: "Digital Permit-to-Work (PTW) system covering hazard identification, safety checks, gas tests, and formal approvals.",
    features: [
      "8-step digital permit wizard",
      "Activity-specific hazard identification",
      "Personnel and contractor management",
      "Gas test logging and verification",
      "Isolation and LOTO confirmation",
    ],
    version: "2.0.0",
    primaryPath: "/home/work-permits/dashboard",
    navKeys: ["wp-dashboard", "wp-list"],
  },
  {
    code: "BI_DASHBOARD",
    name: "BI Dashboard",
    iconEmoji: "📈",
    colorHex: "#7C3AED",
    category: "Analytics",
    description: "Customizable Business Intelligence dashboards with drag-and-drop widgets, real-time KPIs, and data visualization.",
    features: [
      "Personalized drag-and-drop layout",
      "KPI library for OEE, Safety, and PM",
      "Real-time data visualization cards",
      "Cross-plant performance comparisons",
      "Mobile-optimized dashboard view",
    ],
    version: "1.5.0",
    primaryPath: "/home/bi-dashboard",
    navKeys: ["bi-dashboard"],
  },
  {
    code: "REPORTS",
    name: "Reports Hub",
    iconEmoji: "📊",
    colorHex: "var(--accent-primary)",
    category: "Analytics",
    description: "Centralized hub for all system reports — OEE, Maintenance, Safety, and Personnel compliance analytics.",
    features: [
      "OEE Summary and Financial loss reports",
      "PM compliance and overdue analytics",
      "Reliability reports (MTBF/MTTR)",
      "Safety observation and hazard trends",
      "Exportable data for Excel and PDF",
    ],
    version: "2.0.0",
    primaryPath: "/home/reports",
    navKeys: ["reports-hub", "REPORTS"],
  },
  {
    code: "AI_DASHBOARD",
    name: "AI Dashboard",
    iconEmoji: "🤖",
    colorHex: "#6366f1",
    category: "Analytics",
    description: "Hybrid AI-driven dashboard with real-time KPIs and an MaX Elite AI that can query and visualize any plant data instantly.",
    features: [
      "Natural language Copilot chat",
      "Dynamic data visualization generation",
      "Auto-generated actionable insights",
      "Pin and save custom charts",
      "Instant loading Layer 1 KPIs"
    ],
    version: "1.0.0",
    primaryPath: "/home/ai-dashboard",
    navKeys: ["ai-dashboard"]
  }
];

/** Category definitions with display order */
export const APP_CATEGORIES = [
  { value: "all", label: "All Apps" },
  { value: "Continuous Improvement", label: "Continuous Improvement" },
  { value: "Production", label: "Production" },
  { value: "Quality", label: "Quality" },
  { value: "Maintenance", label: "Maintenance" },
  { value: "Safety", label: "Safety" },
  { value: "Analytics", label: "Analytics" },
  { value: "Knowledge Management", label: "Knowledge Management" },
  { value: "Operations", label: "Operations" },
];
